import styled from '@emotion/styled'

export const Background = styled.div({
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #7e22ce 100%)",
    overflow: "auto",
    textAlign: "center"
})

export const GameInfo = styled.div({
    position: "fixed",
    bottom: 10,
    backgroundColor: "rgba(30, 41, 59, 0.95)",
    backdropFilter: "blur(10px)",
    width: "95%",
    maxWidth: "1400px",
    textAlign: "center",
    margin: "auto",
    borderRadius: 16,
    padding: "20px 15px",
    left: "50%",
    transform: "translateX(-50%)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    color: "#ffffff",
    "@media (max-width: 768px)": {
        width: "100%",
        padding: "12px 8px 8px",
        bottom: 0,
        left: 0,
        transform: "none",
        fontSize: "11px",
        borderRadius: "16px 16px 0 0",
        maxHeight: "40vh",
        overflowY: "auto"
    }
})

export const CardInfo = styled.div({
    position: "fixed",
    top: "60px",
    bottom: "74px",
    left: 0,
    right: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 20px",
    "@media (max-width: 768px)": {
        top: "136px",
        bottom: "76px",
        padding: "0 5px",
        alignItems: "stretch",
        justifyContent: "flex-start",
        overflowY: "auto",
    }
})

export const CardHand = styled.div({
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-end",
    width: "100%",
    height: "450px",
    perspective: "1000px",
    overflow: "hidden",
    "@media (max-width: 768px)": {
        height: "auto",
        alignItems: "center",
        overflow: "visible"
    }
})

export const SelectContainer = styled.div({
    position:"fixed",
    top: 70,
    left: "50%",
    transform: "translateX(-50%)",
    width: "90%",
    maxWidth: "400px",
    zIndex: 100,
    '& > div': {
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
    },
    "@media (max-width: 768px)": {
        position: "relative",
        top: "auto",
        left: "auto",
        transform: "none",
        width: "100%",
        maxWidth: "140px",
        fontSize: "11px"
    }
})

export const Stack = styled.div({
    textAlign: "center",
    marginBottom: "5px"
})

export const ListItem = styled.div({
    borderColor: "#003262",
    backgroundColor: "#0047d6",
    color: "#ffffff",
    padding: "10px",
    marginBottom: "5px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
})

export const List = styled.div({
    padding: "5px",
    width: "61.5%",
    display: "flex",
    flexDirection: "column",
    margin: "auto"
})

type ButtonProps = {
    backgroundColor?: string,
    disabled?: boolean,
    width?: string,
    height?: number
}

const buttonBackgroundColor = (props:ButtonProps):string => {
    if (props.disabled) {
        return "rgba(100, 116, 139, 0.5)"
    } else if (props.backgroundColor) {
        return props.backgroundColor
    }
    return 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)'
}

function buttonWidth(props:ButtonProps, defaultValue:string) {
    return props.width ? props.width : defaultValue
}

function buttonHeight(props:ButtonProps, defaultValue:number) {
    return props.height ? props.height : defaultValue
}

// using position:fixed
export const GameButton = styled.button(
    {
        marginLeft: 6,
        marginRight: 6,
        marginTop: 8,
        marginBottom: 8,
        border: "none",
        borderRadius: 10,
        fontSize: "clamp(12px, 2vw, 15px)",  // Fluid font size
        fontWeight: 600,
        cursor: "pointer",
        transition: "all 0.2s ease",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
        "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 6px 16px rgba(0, 0, 0, 0.3)"
        },
        "&:active": {
            transform: "translateY(0px)"
        },
        "@media (max-width: 1024px)": {
            fontSize: "14px",
            marginLeft: 4,
            marginRight: 4
        },
        "@media (max-width: 768px)": {
            marginLeft: 3,
            marginRight: 3,
            marginTop: 4,
            marginBottom: 4,
            fontSize: 14,
            borderRadius: 10,
            padding: "0 16px"
        },
        "@media (max-width: 480px)": {
            fontSize: "13px",
            marginLeft: 2,
            marginRight: 2,
            borderRadius: 8
        }
    },
    (props:ButtonProps) => ({
        background: buttonBackgroundColor(props),
        color: props.disabled ? 'rgba(255, 255, 255, 0.5)': 'white',
        width: buttonWidth(props, "auto"),
        minWidth: "clamp(60px, 15vw, 120px)",  // Fluid min-width
        height: buttonHeight(props, 42),
        padding: "0 20px",
        cursor: props.disabled ? "not-allowed" : "pointer",
        opacity: props.disabled ? 0.6 : 1,
        "&:hover": props.disabled ? {
            transform: "none",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)"
        } : undefined,
        "@media (max-width: 1024px)": {
            height: 36,
            padding: "0 16px"
        },
        "@media (max-width: 768px)": {
            minWidth: "80px",
            height: 44,
            padding: "0 18px",
            fontSize: "14px",
            whiteSpace: "nowrap"
        },
        "@media (max-width: 480px)": {
            height: 40,
            padding: "0 14px",
            minWidth: "70px",
            fontSize: "13px"
        }
    })
)

export const LobbyButton = styled.button(
    {
        marginLeft: 10,
        marginRight: 10,
        marginTop: 10,
        marginBottom: 10,
        border: "none",
        borderRadius: 12,
        fontSize: 16,
        fontWeight: 600,
        cursor: "pointer",
        transition: "all 0.2s ease",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
        "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 6px 16px rgba(0, 0, 0, 0.4)"
        },
        "&:active": {
            transform: "translateY(0px)"
        },
        "@media (max-width: 768px)": {
            marginLeft: 5,
            marginRight: 5,
            marginTop: 6,
            marginBottom: 6,
            fontSize: 14,
            borderRadius: 10
        }
    },
    (props:ButtonProps) => ({
        background: buttonBackgroundColor(props),
        color: props.disabled ? 'rgba(255, 255, 255, 0.5)': 'white',
        width: buttonWidth(props, "400px"),
        maxWidth: "90%",
        height: buttonHeight(props, 50),
        cursor: props.disabled ? "not-allowed" : "pointer",
        opacity: props.disabled ? 0.6 : 1,
        "&:hover": props.disabled ? {
            transform: "none",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
        } : undefined,
        "@media (max-width: 768px)": {
            height: 44
        }
    })
)

export const HomePageButton = styled.button(
    {
        marginTop: 10,
        marginBottom: 10,
        border: "none",
        borderRadius: 12,
        fontSize: 16,
        fontWeight: 600,
        cursor: "pointer",
        transition: "all 0.2s ease",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
        "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 6px 16px rgba(0, 0, 0, 0.4)"
        },
        "&:active": {
            transform: "translateY(0px)"
        }
    },
    (props:ButtonProps) => ({
        background: buttonBackgroundColor(props),
        color: props.disabled ? 'rgba(255, 255, 255, 0.5)': 'white',
        width: buttonWidth(props, "400px"),
        maxWidth: "90%",
        height: buttonHeight(props, 50),
        cursor: props.disabled ? "not-allowed" : "pointer",
        opacity: props.disabled ? 0.6 : 1,
        "&:hover": props.disabled ? {
            transform: "none",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
        } : undefined
    })
)

export const StyledInput = styled.input({
    width: "400px",
    maxWidth: "90%",
    height: "50px",
    padding: "0 20px",
    fontSize: "16px",
    fontWeight: 500,
    border: "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "12px",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    color: "#1e293b",
    outline: "none",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
    "&::placeholder": {
        color: "rgba(30, 41, 59, 0.5)"
    },
    "&:focus": {
        border: "2px solid #60a5fa",
        backgroundColor: "rgba(255, 255, 255, 1)",
        boxShadow: "0 4px 16px rgba(96, 165, 250, 0.4)"
    }
})

export const ContentCard = styled.div({
    maxWidth: "1200px",
    margin: "40px auto",
    padding: "30px",
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
    color: "#1e293b",
    textAlign: "left",
    "& h1": {
        color: "#1e3a8a",
        fontSize: "32px",
        marginBottom: "20px",
        textAlign: "center"
    },
    "& h2": {
        color: "#1e40af",
        fontSize: "24px",
        marginTop: "30px",
        marginBottom: "15px"
    },
    "& h3": {
        color: "#2563eb",
        fontSize: "20px",
        marginTop: "20px",
        marginBottom: "10px"
    },
    "& h4": {
        color: "#3b82f6",
        fontSize: "18px",
        marginTop: "15px",
        marginBottom: "8px"
    },
    "& p": {
        lineHeight: "1.8",
        marginBottom: "12px"
    },
    "& ul": {
        marginLeft: "20px",
        marginBottom: "15px"
    },
    "& li": {
        marginBottom: "8px",
        lineHeight: "1.6"
    }
})

export const RivalOrderPanel = styled.div({
    position: "fixed",
    top: 80,
    right: 20,
    backgroundColor: "rgba(30, 41, 59, 0.95)",
    backdropFilter: "blur(10px)",
    borderRadius: 12,
    padding: "12px 16px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    color: "#ffffff",
    fontSize: "14px",
    maxWidth: "250px",
    zIndex: 50,
    maxHeight: "calc(100vh - 200px)",
    overflowY: "auto",
    "@media (max-width: 1024px)": {
        maxWidth: "200px",
        fontSize: "12px",
        padding: "10px 12px",
        right: 10
    },
    "@media (max-width: 768px)": {
        display: "none"
    }
})

export const GameHeader = styled.div({
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: "rgba(30, 41, 59, 0.98)",
    backdropFilter: "blur(10px)",
    padding: "8px 20px",
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.4)",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    zIndex: 100,
    minHeight: "56px",
    color: "#ffffff",
    "@media (max-width: 768px)": {
        padding: "6px 10px",
        gap: "4px",
        minHeight: "128px",
        flexDirection: "column",
        alignItems: "stretch"
    }
})

export const HeaderTitle = styled.h1({
    margin: 0,
    fontSize: "32px",
    fontWeight: 700,
    background: "linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    transition: "all 0.2s ease",
    "&:hover": {
        opacity: 0.8,
        transform: "translateY(-1px)"
    },
    "@media (max-width: 768px)": {
        fontSize: "16px",
        flexShrink: 0,
        width: "100%",
        textAlign: "center",
        marginBottom: "4px"
    }
})

export const ActionBar = styled.div({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    "@media (max-width: 768px)": {
        gap: "4px",
        width: "100%",
        justifyContent: "flex-start",
        flexWrap: "nowrap",
        overflowX: "auto"
    }
})

export const ActionPanel = styled.div({
    position: "fixed",
    bottom: "10px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "rgba(30, 41, 59, 0.98)",
    backdropFilter: "blur(10px)",
    padding: "12px 20px",
    borderRadius: "16px",
    boxShadow: "0 -2px 12px rgba(0, 0, 0, 0.4)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    zIndex: 100,
    maxWidth: "95%",
    flexWrap: "wrap",
    "@media (max-width: 1024px)": {
        padding: "10px 16px",
        gap: "10px"
    },
    "@media (max-width: 768px)": {
        padding: "10px 12px",
        gap: "8px",
        bottom: "8px",
        maxWidth: "98%"
    },
    "@media (max-width: 480px)": {
        padding: "8px 10px",
        gap: "6px",
        bottom: "3px",
        borderRadius: "12px"
    }
})

export const SelectedCardsTable = styled.div({
    position: "fixed",
    top: 72,
    left: 20,
    backgroundColor: "rgba(30, 41, 59, 0.95)",
    backdropFilter: "blur(10px)",
    borderRadius: 12,
    padding: "12px",
    minWidth: "100px",
    maxWidth: "140px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
    border: "2px solid rgba(96, 165, 250, 0.4)",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "flex-start",
    gap: "8px",
    zIndex: 40,
    "@media (max-width: 1024px)": {
        top: 98,
        left: 15,
        padding: "10px",
        maxWidth: "120px",
        gap: "6px"
    },
    "@media (max-width: 768px)": {
        display: "none"
    }
})

export const WinningContainer = styled.div({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    padding: "40px 20px",
    textAlign: "center"
})

export const WinningCard = styled.div({
    backgroundColor: "rgba(30, 41, 59, 0.95)",
    backdropFilter: "blur(20px)",
    borderRadius: "24px",
    padding: "48px 40px",
    maxWidth: "600px",
    width: "90%",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "32px"
})

export const WinningTitle = styled.h1({
    margin: 0,
    fontSize: "48px",
    fontWeight: 700,
    background: "linear-gradient(135deg, #60a5fa 0%, #a78bfa 50%, #fbbf24 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    lineHeight: 1.2,
    "@media (max-width: 768px)": {
        fontSize: "36px"
    }
})

export const WinningMessage = styled.p({
    margin: 0,
    fontSize: "20px",
    fontWeight: 500,
    color: "#e2e8f0",
    lineHeight: 1.6,
    "@media (max-width: 768px)": {
        fontSize: "18px"
    }
})

export const LobbyContainer = styled.div({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 20px",
    width: "100%",
    height: "100%",
    overflowY: "auto",
    "@media (max-width: 768px)": {
        padding: "20px 10px"
    }
})

export const LobbyCard = styled.div({
    backgroundColor: "rgba(30, 41, 59, 0.95)",
    backdropFilter: "blur(20px)",
    borderRadius: "24px",
    padding: "40px",
    maxWidth: "700px",
    width: "90%",
    maxHeight: "calc(100vh - 80px)",
    overflowY: "auto",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "24px",
    color: "#ffffff",
    margin: "20px 0",
    "@media (max-width: 768px)": {
        padding: "20px 16px",
        borderRadius: "16px",
        gap: "16px",
        width: "95%",
        maxHeight: "calc(100vh - 40px)",
        margin: "10px 0"
    }
})

export const LobbyTitle = styled.h1({
    margin: 0,
    fontSize: "36px",
    fontWeight: 700,
    background: "linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    textAlign: "center",
    "@media (max-width: 768px)": {
        fontSize: "24px"
    }
})

export const LobbyText = styled.p({
    margin: 0,
    fontSize: "16px",
    fontWeight: 500,
    color: "#e2e8f0",
    lineHeight: 1.6,
    textAlign: "center",
    "@media (max-width: 768px)": {
        fontSize: "14px"
    }
})

export const GameIdDisplay = styled.div({
    backgroundColor: "rgba(96, 165, 250, 0.15)",
    padding: "12px 24px",
    borderRadius: "12px",
    border: "2px solid rgba(96, 165, 250, 0.4)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontSize: "18px",
    fontWeight: 700,
    color: "#60a5fa",
    fontFamily: "monospace",
    letterSpacing: "1px",
    "&:hover": {
        backgroundColor: "rgba(96, 165, 250, 0.25)",
        borderColor: "rgba(96, 165, 250, 0.6)",
        transform: "translateY(-2px)",
        boxShadow: "0 4px 12px rgba(96, 165, 250, 0.3)"
    },
    "@media (max-width: 768px)": {
        padding: "10px 16px",
        fontSize: "16px",
        borderRadius: "8px"
    }
})

export const PlayersList = styled.div({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "12px"
})

export const PlayersListItem = styled.div({
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    padding: "16px 20px",
    borderRadius: "12px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: "all 0.2s ease",
    "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.08)",
        borderColor: "rgba(255, 255, 255, 0.2)"
    },
    "@media (max-width: 768px)": {
        padding: "12px 16px",
        borderRadius: "8px",
        fontSize: "14px"
    }
})

// Compact horizontal player strip — only visible on mobile
// Placed inside GameHeader as the last row to show all players' card counts
export const MobilePlayerStrip = styled.div({
    display: "none",
    "@media (max-width: 768px)": {
        display: "flex",
        gap: "6px",
        overflowX: "auto",
        flexWrap: "nowrap",
        padding: "3px 0 1px",
        alignItems: "center",
        width: "100%",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": { display: "none" }
    }
})
